import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl } from '../../utils/apiUrl';
import { TIMEOUTS } from '../../config/timeouts';

// SCSS
import '../../assets/scss/loginForm.scss';

// Images
import adrBygg from '../../assets/images/adr_bygg.jpg';

const log = (...args) => {
	if (process.env.NODE_ENV === 'development') {
		console.log('[LoginForm]', ...args);
	}
};

const LoginForm = () => {
	const navigate = useNavigate();
	const { login } = useAuth();

	const [userPass, setUserPass] = useState('password');
	const [mode, setMode] = useState('login');

	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [forcePasswordNotice, setForcePasswordNotice] = useState(false);

	const [loadingMessage, setLoadingMessage] = useState('');
	const [isRedirecting, setIsRedirecting] = useState(false);

	const showPass = (e) => {
		e.preventDefault();
		if (userPass === 'password') {
			setUserPass('text');
		} else {
			setUserPass('password');
		}
	};

	useEffect(() => {
		let count = 0;
		if (isRedirecting) {
			const interval = setInterval(() => {
				setLoadingMessage(`Videresender${'.'.repeat(count % 4)}`);
				count++;
			}, TIMEOUTS.loginRedirectAnimationIntervalMs);

			const timeout = setTimeout(() => {
				clearInterval(interval);
				navigate('/');
			}, TIMEOUTS.loginRedirectDelayMs);

			return () => {
				clearInterval(interval);
				clearTimeout(timeout);
			};
		}
	}, [isRedirecting, navigate]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		setErrorMessage('');
		setForcePasswordNotice(false);
		setLoadingMessage('');

		if (mode === 'login') {
			// Use AuthContext login
			setLoadingMessage('Logger inn...');

			const result = await login({ login: username, password });

			if (result.success) {
				log('innlogging vellykket');
				try {
					localStorage.removeItem('authTimeoutDraftId');
					localStorage.removeItem('authTimeoutReason');
					localStorage.removeItem('authTimeoutSavedAt');
					localStorage.removeItem('authTimeoutLocalDraft');
				} catch {
					/* ignore */
				}
				// Utkast: kun Form.jsx (etter navigasjon til skjema) henter /draft/me og viser Swal.
				log('redirect til forside');
				setIsRedirecting(true);
			} else {
				log('innlogging feilet:', result.error || 'ukjent feil');
				setLoadingMessage('');
				setErrorMessage(result.error || 'Pålogging feilet');
				setForcePasswordNotice(result.code === 'FORCE_PASSWORD_CHANGE');
			}
		} else {
			// Password reset
			try {
				const response = await fetch(buildApiUrl('auth/reset-password'), {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ username }),
					credentials: 'include',
				});

				const data = await response.json();

				if (response.ok) {
					setErrorMessage(
						data.message || 'Klikk på lenken i eposten din for å tilbakestille ditt passord.'
					);
				} else {
					setErrorMessage(data.message || 'Kunne ikke tilbakestille passord');
				}
			} catch (error) {
				console.error('Error:', error);
				setErrorMessage('Kunne ikke prosessere forespørselen');
			}
		}
	};

	return (
		<div
			className='loginForm'
			style={{ backgroundImage: `url(${adrBygg})` }}
		>
			<div className='formContainer'>
				<h2>
					{mode === 'login' ? ' Logg inn' : 'Tilbakestill passord'}
				</h2>
				<div className='form'>
					<form onSubmit={handleSubmit}>
						<div>
							<label htmlFor='emailUser'>
								{mode === 'login'
									? 'E-post eller brukernavn'
									: 'E-post'}
							</label>
							<input
								type='text'
								placeholder='Epost / Brukernavn'
								name='username'
								value={username}
								onChange={(e) => setUsername(e.target.value)}
								id='emailUser'
								autoComplete={
									mode === 'login' ? 'username' : 'email'
								}
								autoCapitalize='none'
								spellCheck='false'
							/>
						</div>
						{mode === 'login' && (
							<div className='passField'>
								<label htmlFor='loginPassword'>Passord</label>
								<input
									type={userPass}
									placeholder='*******'
									name='password'
									id='loginPassword'
									value={password}
									autoComplete='current-password'
									onChange={(e) =>
										setPassword(e.target.value)
									}
								/>
								<button
									type='button'
									onClick={showPass}
									aria-label={
										userPass === 'password'
											? 'Vis passord'
											: 'Skjul passord'
									}
								>
									{userPass === 'password' ? 'Vis' : 'Skjul'}
								</button>
							</div>
						)}
						<input
							type='submit'
							value={
								mode === 'login'
									? 'Logg inn'
									: 'Tilbakestill passord'
							}
						/>
						<button
							type='button'
							className='forgotPass_btn'
							onClick={() =>
								setMode(
									mode === 'login'
										? 'forgotPassword'
										: 'login'
								)
							}
						>
							{mode === 'login'
								? 'Glemt passord'
								: 'Tilbake til innlogging'}
						</button>
					</form>
					{errorMessage && (
						<div
							className={
								forcePasswordNotice
									? 'loginForm__alert loginForm__alert--force-password'
									: 'loginForm__alert'
							}
							role='alert'
						>
							<p>{errorMessage}</p>
						</div>
					)}
					{loadingMessage && (
						<p className='loginForm__loading'>{loadingMessage}</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default LoginForm;
